import { Util } from '@antv/g6';
import { AnchorPointState, AnchorType } from '../../../../constants';
import { AnchorTuple, DrawOpts, Group, NodeModel } from '../../../../types';
import { groupIds, shapeNames } from '../../../constants';
import {
  addEllipsisToLineIfNeeded,
  addTooltip,
  getAnchorGroupId,
  getAnchorPoints,
  getAnchorTypeStyle,
  getChild,
  hasTooltip,
  removeTooltip,
  updateTooltip,
} from '../../../utils';

const getAnchorTitle = (anchorPoint: AnchorTuple, { options }: Pick<DrawOpts, 'options'>) => {
  const { padding, maxWidth } = options[groupIds.anchor];
  const [, paddingRight, , paddingLeft] = Util.formatPadding(padding);
  const [, , title] = anchorPoint;
  const text = title || '';

  return {
    full: text,
    truncated: addEllipsisToLineIfNeeded(text, {
      ...options[shapeNames.anchorTitle],
      maxWidth: maxWidth - paddingLeft - paddingRight,
    }),
  };
};

interface DrawAnchor extends Omit<DrawOpts, 'model'> {
  anchorPoint: AnchorTuple;
  anchorType?: AnchorType;
  groupId: string;
  anchorPointIndex: number;
}

/**
 * Draw an individual anchor
 */
const drawAnchor = ({
  size,
  anchorPoint,
  options,
  anchorType = AnchorType.In,
  groupId,
  group,
  anchorPointIndex,
}: DrawAnchor) => {
  const [nodeWidth, height] = size;
  const { accentColor, activeBorderColor } = options;
  const anchorStyle = options[shapeNames.anchor];
  const { stroke, lineWidth } = anchorStyle;
  const [x, y] = anchorPoint;
  const nodeX = nodeWidth * x;
  const nodeY = height * y;
  const title = getAnchorTitle(anchorPoint, { options });
  const anchorGroup = group.addGroup({ id: groupId });
  const textShape = anchorGroup.addShape('text', {
    name: shapeNames.anchorTitle,
    className: shapeNames.anchorTitle,
    attrs: {
      text: title.truncated,
      x: nodeX,
      y: nodeY - 10,
      fill: accentColor,
      ...options[shapeNames.anchorTitle],
    },
  });

  if (title.truncated.includes('...')) {
    addTooltip({ shape: textShape, text: title.full });
  }

  const className = `${shapeNames.anchor}-${anchorType}`;

  return anchorGroup.addShape('circle', {
    name: className,
    className,
    draggable: true,
    attrs: {
      ...getAnchorTypeStyle(anchorType, anchorStyle),
      x: nodeX,
      y: nodeY,
      activeBorderColor,
      activeLineWidth: 2,
      defaultBorderColor: stroke,
      defaultLineWidth: lineWidth,
    },
    isAnchorPoint: true,
    anchorPointIndex,
    anchorPointState: AnchorPointState.Enabled,
  });
};

interface DrawAnchorGroup extends Omit<DrawOpts, 'model'> {
  anchors?: AnchorTuple[];
  anchorType?: AnchorType;
  indexOffset?: number;
}

const drawAnchorGroup = (args: DrawAnchorGroup) => {
  const { anchors = [], anchorType = AnchorType.Out, indexOffset = 0, ...rest } = args;
  return anchors.map((anchorPoint, i) =>
    drawAnchor({
      anchorPoint,
      groupId: getAnchorGroupId(anchorType, i),
      anchorPointIndex: i + indexOffset,
      anchorType,
      ...rest,
    }),
  );
};

export const drawAnchors = ({ size, options, group, model }: DrawOpts) => {
  const { inputs, outputs } = getAnchorPoints(model as NodeModel) || {};

  const inGroup = group.addGroup({ id: groupIds.anchorIn });
  const outGroup = group.addGroup({ id: groupIds.anchorOut });

  return [
    drawAnchorGroup({
      size,
      options,
      anchors: inputs,
      anchorType: AnchorType.In,
      indexOffset: 0,
      group: inGroup,
    }),
    drawAnchorGroup({
      size,
      options,
      anchors: outputs,
      anchorType: AnchorType.Out,
      indexOffset: inputs.length,
      group: outGroup,
    }),
  ];
};

const getOutGroup = (group: DrawOpts['group']): Group | null => getChild(group, groupIds.anchorOut);

export const updateAnchors = ({ size, options, group, model }: DrawOpts) => {
  const { inputs, outputs } = getAnchorPoints(model as NodeModel) || {};
  const outGroup = getOutGroup(group);
  if (!outGroup) return;

  const outAnchors: Group[] = outGroup.get('children') || [];

  if (outAnchors.length !== outputs.length) {
    // If the number of anchors has changed, rerender the whole thing as there
    // could be a whole bunch of positioning changes
    outGroup.clear();
    drawAnchorGroup({
      size,
      options,
      anchors: outputs,
      anchorType: AnchorType.Out,
      indexOffset: inputs.length,
      group: outGroup,
    });
  } else {
    outAnchors.forEach((o: Group, i: number) => {
      const titleShape = o?.findByClassName(shapeNames.anchorTitle);
      const newTitle = getAnchorTitle(outputs[i], { options });
      // If the title has changed, update it
      if (titleShape && titleShape?.attr()?.text !== newTitle.truncated) {
        titleShape.attr('text', newTitle.truncated);
      }
      if (!newTitle.truncated.includes('...')) {
        removeTooltip(titleShape);
      } else if (hasTooltip(titleShape)) {
        updateTooltip({ shape: titleShape, tooltipText: newTitle.full });
      } else {
        addTooltip({ shape: titleShape, text: newTitle.full });
      }
    });
  }
};
